import React, { useCallback, useMemo, useState } from "react";

import { GanttProps } from "components/ComponentType";
import { IProductTemplate, ITableDataEntry } from "components/dashboard/product-template-table/utils/interfaces";
import { Config, Data, Layout, PlotData, Shape } from "plotly.js";
import Plot from "react-plotly.js";
import { T } from "services/lang/LanguageService";
import { formatDate, formatDateDuration } from "utils/time-utils";
import copyChartToClipBoard from "../../chartUtils";
import { clipboardIcon } from "../../icons";

type GanttChartProps = GanttProps & {
  templateData: ITableDataEntry[],
  configData: IProductTemplate | undefined,
}
const OFFSET_HIGH = 0.4;
const MAX_VISIBLE_LABELS = 40;

const GanttChart: React.FC<GanttChartProps> = ({templateData, configData, ...props}) => {
  const getAdditionalInfo = useCallback((tableData: ITableDataEntry) => {
    return Object.values(tableData.entries).reduce((arr, val) => {
      if (val.type !== "INSTANT") {
        return `${arr}<br>${configData?.data?.attributes.find(a => a.referenceName === val.referenceName)?.displayName}: ${val.value}`;
      }
      return arr;
    }, "");
  }, [configData?.data?.attributes]);

  const ranges: Partial<PlotData>[] = useMemo(
    () =>
      templateData.reduce((array, acc, index) => {
        props.ganttRanges.forEach(gr => {
          const startDate = acc.entries[gr.startDateColumn!]?.value?.toString();
          const endDate = acc.entries[gr.endDateColumn!]?.value?.toString() ?? new Date().toISOString();
          const trace: Partial<PlotData> = {
            mode: "lines",
            fill: "toself",
            opacity: 0,
            name: gr.title ?? "",
            x: [startDate, endDate, endDate, startDate, startDate],
            y: [index - OFFSET_HIGH, index - OFFSET_HIGH, index + OFFSET_HIGH, index + OFFSET_HIGH, index - OFFSET_HIGH],
            marker: { color: gr.color },
            showlegend: false,
            text:
              `${gr.title}<br>` +
              `${T("layout.start")}: ${formatDate(startDate)}<br>` +
              `${T("layout.end")}: ${formatDate(endDate)}<br>` +
              `${T("word.duration")}: ${formatDateDuration(startDate, endDate)}` +
              `${configData && getAdditionalInfo(acc)}`,
            hoverinfo: "text",
          };
          if (startDate) array.push(trace);
        });
        return array;
      }, [] as Partial<PlotData>[]),
    [configData, getAdditionalInfo, props.ganttRanges, templateData]
  );

  const timestamps: Partial<PlotData>[] = useMemo(
    () =>
      templateData.reduce((array, acc, index) => {
        props.ganttTimestamps.forEach(gt => {
          const date = acc.entries[gt.dateColumn!]?.value?.toString();
          const trace: Partial<PlotData> = {
            name: gt.title ?? "",
            x: [date, date],
            y: [index, index],
            marker: { color: gt.color, symbol: gt.icon ?? "circle", size: 10 },
            showlegend: false,
            text:
              `${gt.title}<br>` +
              `${T("word.date")}: ${formatDate(date)}` +
              `${configData && getAdditionalInfo(acc)}`,
            hoverinfo: "text",
          };
          array.push(trace);
        });
        return array;
      }, [] as Partial<PlotData>[]),
    [configData, getAdditionalInfo, props.ganttTimestamps, templateData]
  );

  const yLabels = useMemo(
    () =>
      templateData
        ?.sort((a, b) => a.dateCreated.localeCompare(b.dateCreated))
        .map(
          td =>
            td.entries[props.columnOverrideYLabels]?.value?.toString() ??
            td.stringRepresentation ??
            "-"
        ),
    [props.columnOverrideYLabels, templateData]
  );

  const data: Data[] = [...ranges, ...timestamps];
  const layout: Partial<Layout> = useMemo(
    () => ({
      xaxis: {
        type: "date",
        showgrid: true,
        zeroline: false,
      },
      yaxis: {
        range: [-1, templateData.length],
        showgrid: true,
        ticktext: yLabels,
        showticklabels: yLabels.length <= MAX_VISIBLE_LABELS,
        tickvals: templateData.map((_td, i) => i),
        zeroline: false,
        autorange: false,
      },
      height: props.height ?? 500,
      shapes: ranges.map(tr => {
        return {
          x0: tr.x![0],
          x1: tr.x![1],
          y0: +tr.y![0]!,
          y1: +tr.y![0]! + OFFSET_HIGH * 2,
          line: { width: 4, color: tr.marker?.color },
          type: "rect",
          xref: "x",
          yref: "y",
          opacity: 0.8,
          fillcolor: props.ganttRanges.find(gr => gr.title === tr.name)?.color,
        };
      }) as Partial<Shape>[],
      hovermode: "closest",
      showlegend: false,
      legend: { orientation: "h" },
      margin: {
        l: Math.max(...yLabels.map(y => y.length)) * 7,
        r: 0,
        b: 20,
        t: 20,
      },
    }),
    [props.ganttRanges, props.height, ranges, templateData, yLabels]
  );
  const [currentLayout, setCurrentLayout] = useState(layout);

  const handleZoom = useCallback((eventData: Readonly<Plotly.PlotRelayoutEvent>) => {
    const yRange0 = eventData["yaxis.range[0]"];
    const yRange1 = eventData["yaxis.range[1]"];
    if (!yRange0 || !yRange1) return;
    const visibleLabels = Math.abs(yRange1 - yRange0);
    const showLabels = visibleLabels <= MAX_VISIBLE_LABELS;

    const newLayout = {
      ...currentLayout,
      yaxis: {
        ...currentLayout.yaxis,
        showticklabels: showLabels,
      },
    };

    setCurrentLayout(newLayout);
  }, [currentLayout]);

  const chartConfig: Partial<Config> = {
    displaylogo: false,
    modeBarButtonsToRemove: ["lasso2d", "select2d"],
    modeBarButtonsToAdd: [
      {
        title: T("chart-config.copy-chart-to-clipboard"),
        name: "Copy chart to Clipboard",
        icon: clipboardIcon,
        click: copyChartToClipBoard,
      },
    ],
  };

  return (
    <Plot
      data={data}
      layout={currentLayout}
      config={chartConfig}
      className="w-full"
      useResizeHandler
      onRelayout={handleZoom}
    />
  );
};

export default GanttChart;
